/**
 * Gaussian RBF interpolation for 3D scalar fields with data-driven anisotropy.
 *
 * Given scattered 3D sample points with indicator values (0/1), this module:
 * 1. Optionally fits a best-fit plane through "inside" samples via PCA
 * 2. Evaluates the interpolated field on a regular 3D grid using anisotropic RBF
 * 3. Extracts an isosurface via marching cubes
 *
 * The anisotropy flattens the influence ellipsoid along the plane normal,
 * so the resulting surface is slab/plane-like rather than blobby.
 */

export interface RBFSample {
  x: number;
  y: number;
  z: number;
  value: number;
}

// ─── PCA: find best-fit plane normal from "inside" samples ───────────────────

/**
 * Compute the normal of the best-fit plane through the given points using PCA.
 * Returns the eigenvector corresponding to the smallest eigenvalue of the
 * covariance matrix — i.e. the direction of least spread (the plane normal).
 */
export function fitPlaneNormal(points: { x: number; y: number; z: number }[]): [number, number, number] {
  const n = points.length;
  if (n < 3) return [0, 1, 0]; // fallback

  // Centroid
  let cx = 0, cy = 0, cz = 0;
  for (const p of points) { cx += p.x; cy += p.y; cz += p.z; }
  cx /= n; cy /= n; cz /= n;

  // 3x3 covariance matrix (symmetric)
  let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0;
  for (const p of points) {
    const dx = p.x - cx, dy = p.y - cy, dz = p.z - cz;
    xx += dx * dx; xy += dx * dy; xz += dx * dz;
    yy += dy * dy; yz += dy * dz; zz += dz * dz;
  }
  xx /= n; xy /= n; xz /= n; yy /= n; yz /= n; zz /= n;

  // Find smallest eigenvector of 3x3 symmetric matrix using power iteration
  // on the inverse (inverse iteration). For robustness, use direct analytic approach.
  // We'll use iterative deflation: find largest eigenvector, deflate, repeat.
  
  // Power iteration for largest eigenvector
  function powerIteration(
    a00: number, a01: number, a02: number,
    a11: number, a12: number, a22: number,
  ): { vec: [number, number, number]; val: number } {
    let vx = 1, vy = 0, vz = 0;
    for (let iter = 0; iter < 50; iter++) {
      const nx = a00 * vx + a01 * vy + a02 * vz;
      const ny = a01 * vx + a11 * vy + a12 * vz;
      const nz = a02 * vx + a12 * vy + a22 * vz;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      if (len < 1e-14) break;
      vx = nx / len; vy = ny / len; vz = nz / len;
    }
    const val = (a00 * vx + a01 * vy + a02 * vz) * vx +
                (a01 * vx + a11 * vy + a12 * vz) * vy +
                (a02 * vx + a12 * vy + a22 * vz) * vz;
    return { vec: [vx, vy, vz], val };
  }

  // Find first (largest) eigenvector
  const e1 = powerIteration(xx, xy, xz, yy, yz, zz);

  // Deflate: subtract outer product of first eigenvector * eigenvalue
  const d00 = xx - e1.val * e1.vec[0] * e1.vec[0];
  const d01 = xy - e1.val * e1.vec[0] * e1.vec[1];
  const d02 = xz - e1.val * e1.vec[0] * e1.vec[2];
  const d11 = yy - e1.val * e1.vec[1] * e1.vec[1];
  const d12 = yz - e1.val * e1.vec[1] * e1.vec[2];
  const d22 = zz - e1.val * e1.vec[2] * e1.vec[2];

  // Find second eigenvector from deflated matrix
  const e2 = powerIteration(d00, d01, d02, d11, d12, d22);

  // Third eigenvector = cross product of first two (smallest eigenvalue direction)
  const nx = e1.vec[1] * e2.vec[2] - e1.vec[2] * e2.vec[1];
  const ny = e1.vec[2] * e2.vec[0] - e1.vec[0] * e2.vec[2];
  const nz2 = e1.vec[0] * e2.vec[1] - e1.vec[1] * e2.vec[0];
  const len = Math.sqrt(nx * nx + ny * ny + nz2 * nz2);
  if (len < 1e-10) return [0, 1, 0];
  return [nx / len, ny / len, nz2 / len];
}

// ─── Anisotropic Gaussian RBF evaluation ─────────────────────────────────────

const MAX_RBF_CONTROLS = 500;
const RBF_REGULARIZATION = 0.03;

/**
 * Pick a deterministic, class-balanced subset for the dense RBF solve. The full
 * MWD set can be 20k+ points; solving that matrix in the browser is unnecessary.
 */
function pickControlSamples(samples: RBFSample[]): RBFSample[] {
  if (samples.length <= MAX_RBF_CONTROLS) return samples;

  const inside = samples.filter((s) => s.value > 0.5);
  const outside = samples.filter((s) => s.value <= 0.5);

  function takeEvenly(source: RBFSample[], count: number): RBFSample[] {
    if (count <= 0) return [];
    if (source.length <= count) return source;
    if (count === 1) return [source[Math.floor(source.length / 2)]];
    const picked: RBFSample[] = [];
    const step = (source.length - 1) / (count - 1);
    for (let i = 0; i < count; i++) picked.push(source[Math.round(i * step)]);
    return picked;
  }

  const insideRatio = inside.length / samples.length;
  let insideCount = Math.round(MAX_RBF_CONTROLS * insideRatio);
  insideCount = Math.max(Math.min(80, inside.length), Math.min(inside.length, MAX_RBF_CONTROLS - Math.min(80, outside.length), insideCount));
  const outsideCount = Math.min(outside.length, MAX_RBF_CONTROLS - insideCount);
  return [...takeEvenly(inside, insideCount), ...takeEvenly(outside, outsideCount)];
}

function anisotropicDistance2(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  planeNormal: [number, number, number],
  flatness: number,
): { rawD2: number; anisoD2: number } {
  const dx = ax - bx;
  const dy = ay - by;
  const dz = az - bz;
  const rawD2 = dx * dx + dy * dy + dz * dz;
  if (flatness <= 1.01) return { rawD2, anisoD2: rawD2 };

  const [pnx, pny, pnz] = planeNormal;
  const normalComp = dx * pnx + dy * pny + dz * pnz;
  const inPlaneD2 = Math.max(0, rawD2 - normalComp * normalComp);
  return { rawD2, anisoD2: inPlaneD2 + normalComp * normalComp * flatness * flatness };
}

function gaussianKernel(anisoD2: number, radius: number, sharpness: number): number {
  const sigma = Math.max(0.25, radius / Math.max(0.5, sharpness));
  return Math.exp(-0.5 * anisoD2 / (sigma * sigma));
}

function solveLinearSystem(a: Float64Array, b: Float64Array, n: number): Float64Array {
  for (let col = 0; col < n; col++) {
    let pivotRow = col;
    let pivotAbs = Math.abs(a[col * n + col]);
    for (let row = col + 1; row < n; row++) {
      const v = Math.abs(a[row * n + col]);
      if (v > pivotAbs) {
        pivotAbs = v;
        pivotRow = row;
      }
    }

    if (pivotAbs < 1e-12) continue;

    if (pivotRow !== col) {
      for (let k = col; k < n; k++) {
        const ai = col * n + k;
        const pi = pivotRow * n + k;
        const tmp = a[ai];
        a[ai] = a[pi];
        a[pi] = tmp;
      }
      const tb = b[col];
      b[col] = b[pivotRow];
      b[pivotRow] = tb;
    }

    const pivot = a[col * n + col];
    for (let row = col + 1; row < n; row++) {
      const factor = a[row * n + col] / pivot;
      if (Math.abs(factor) < 1e-15) continue;
      a[row * n + col] = 0;
      for (let k = col + 1; k < n; k++) a[row * n + k] -= factor * a[col * n + k];
      b[row] -= factor * b[col];
    }
  }

  const x = new Float64Array(n);
  for (let row = n - 1; row >= 0; row--) {
    let sum = b[row];
    for (let col = row + 1; col < n; col++) sum -= a[row * n + col] * x[col];
    const diag = a[row * n + row];
    x[row] = Math.abs(diag) < 1e-12 ? 0 : sum / diag;
  }
  return x;
}

interface RBFModel {
  controls: RBFSample[];
  domainSamples: RBFSample[];
  weights: Float64Array;
  radius: number;
  sharpness: number;
  planeNormal: [number, number, number];
  flatness: number;
}

function buildRBFModel(
  samples: RBFSample[],
  sharpness: number,
  radius: number,
  planeNormal: [number, number, number],
  flatness: number,
): RBFModel {
  const controls = pickControlSamples(samples);
  const n = controls.length;
  const a = new Float64Array(n * n);
  const b = new Float64Array(n);

  for (let i = 0; i < n; i++) {
    b[i] = controls[i].value - 0.5;
    for (let j = i; j < n; j++) {
      const { anisoD2 } = anisotropicDistance2(
        controls[i].x, controls[i].y, controls[i].z,
        controls[j].x, controls[j].y, controls[j].z,
        planeNormal, flatness,
      );
      const v = gaussianKernel(anisoD2, radius, sharpness) + (i === j ? RBF_REGULARIZATION : 0);
      a[i * n + j] = v;
      a[j * n + i] = v;
    }
  }

  return { controls, domainSamples: samples, weights: solveLinearSystem(a, b, n), radius, sharpness, planeNormal, flatness };
}

/**
 * Evaluate anisotropic Gaussian RBF at a single point.
 * planeNormal: the "thin" direction. Distance along this axis is amplified.
 * flatness: how much to amplify distance along the normal.
 *   1 = isotropic (sphere), 5 = 5x thinner along normal (flat slab).
 */
function evaluateRBF(
  model: RBFModel,
  x: number, y: number, z: number,
): number {
  let value = 0.5;
  let nearestRawD2 = Infinity;
  const emptySpaceD2 = model.radius * model.radius * 2.25;

  for (let i = 0; i < model.domainSamples.length; i++) {
    const s = model.domainSamples[i];
    const dx = x - s.x;
    const dy = y - s.y;
    const dz = z - s.z;
    const rawD2 = dx * dx + dy * dy + dz * dz;
    if (rawD2 < nearestRawD2) nearestRawD2 = rawD2;
  }

  // Far from samples is outside the known domain. Marching cubes skips these
  // cells entirely to avoid artificial vertical/box-like boundary sheets.
  if (nearestRawD2 > emptySpaceD2) return NaN;

  for (let i = 0; i < model.controls.length; i++) {
    const c = model.controls[i];
    const { anisoD2 } = anisotropicDistance2(x, y, z, c.x, c.y, c.z, model.planeNormal, model.flatness);
    value += model.weights[i] * gaussianKernel(anisoD2, model.radius, model.sharpness);
  }
  return Math.max(0, Math.min(1, value));
}

// ─── Grid evaluation ─────────────────────────────────────────────────────────

export interface ScalarGrid {
  data: Float32Array;
  nx: number; ny: number; nz: number;
  origin: [number, number, number];
  spacing: [number, number, number];
}

export function evaluateOnGrid(
  samples: RBFSample[],
  origin: [number, number, number],
  size: [number, number, number],
  resolution: [number, number, number],
  sharpness: number,
  radius: number,
  planeNormal: [number, number, number],
  flatness: number,
): ScalarGrid {
  const [nx, ny, nz] = resolution;
  const spacing: [number, number, number] = [
    size[0] / (nx - 1), size[1] / (ny - 1), size[2] / (nz - 1),
  ];
  const model = buildRBFModel(samples, sharpness, radius, planeNormal, flatness);
  const data = new Float32Array(nx * ny * nz);
  let idx = 0;
  for (let iz = 0; iz < nz; iz++) {
    const z = origin[2] + iz * spacing[2];
    for (let iy = 0; iy < ny; iy++) {
      const y = origin[1] + iy * spacing[1];
      for (let ix = 0; ix < nx; ix++) {
        data[idx++] = evaluateRBF(model, origin[0] + ix * spacing[0], y, z);
      }
    }
  }
  return { data, nx, ny, nz, origin, spacing };
}

// ─── Marching Cubes ──────────────────────────────────────────────────────────

import { EDGE_TABLE, TRI_TABLE } from "./marchingCubesTables";

const MC_EDGE_VERTICES: [number, number][] = [
  [0, 1], [1, 2], [2, 3], [3, 0],
  [4, 5], [5, 6], [6, 7], [7, 4],
  [0, 4], [1, 5], [2, 6], [3, 7],
];

const MC_CUBE_OFFSETS: [number, number, number][] = [
  [0, 0, 0], [1, 0, 0], [1, 1, 0], [0, 1, 0],
  [0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1],
];

export function marchingCubes(grid: ScalarGrid, isoValue: number): Float32Array {
  const { data, nx, ny, nz, origin, spacing } = grid;
  const vertices: number[] = [];

  function getVal(ix: number, iy: number, iz: number): number {
    return data[iz * ny * nx + iy * nx + ix];
  }

  for (let iz = 0; iz < nz - 1; iz++) {
    for (let iy = 0; iy < ny - 1; iy++) {
      for (let ix = 0; ix < nx - 1; ix++) {
        const vals = new Array(8);
        const pos: [number, number, number][] = new Array(8);
        for (let ci = 0; ci < 8; ci++) {
          const [cx, cy, cz] = MC_CUBE_OFFSETS[ci];
          const gx = ix + cx, gy = iy + cy, gz = iz + cz;
          vals[ci] = getVal(gx, gy, gz);
          pos[ci] = [
            origin[0] + gx * spacing[0],
            origin[1] + gy * spacing[1],
            origin[2] + gz * spacing[2],
          ];
        }

        if (vals.some((v) => !Number.isFinite(v))) continue;

        let cubeIndex = 0;
        for (let ci = 0; ci < 8; ci++) {
          if (vals[ci] < isoValue) cubeIndex |= (1 << ci);
        }
        if (cubeIndex === 0 || cubeIndex === 255) continue;

        const edges = EDGE_TABLE[cubeIndex];
        const edgeVerts: [number, number, number][] = new Array(12);
        for (let ei = 0; ei < 12; ei++) {
          if (!(edges & (1 << ei))) continue;
          const [a, b] = MC_EDGE_VERTICES[ei];
          const va = vals[a], vb = vals[b];
          const t = Math.abs(vb - va) < 1e-10 ? 0.5 : (isoValue - va) / (vb - va);
          edgeVerts[ei] = [
            pos[a][0] + t * (pos[b][0] - pos[a][0]),
            pos[a][1] + t * (pos[b][1] - pos[a][1]),
            pos[a][2] + t * (pos[b][2] - pos[a][2]),
          ];
        }

        const triRow = TRI_TABLE[cubeIndex];
        for (let i = 0; i < triRow.length; i += 3) {
          if (triRow[i] === -1) break;
          const v0 = edgeVerts[triRow[i]];
          const v1 = edgeVerts[triRow[i + 1]];
          const v2 = edgeVerts[triRow[i + 2]];
          if (v0 && v1 && v2) {
            vertices.push(v0[0], v0[1], v0[2], v1[0], v1[1], v1[2], v2[0], v2[1], v2[2]);
          }
        }
      }
    }
  }

  return new Float32Array(vertices);
}
